/**
 * Weekly Updates Timeline
 *
 * Shows the weekly check-in history for a rock — health status, notes, and who saved.
 * Displayed as a compact timeline in the rock detail view.
 */

import { TrendingUp, TrendingDown, ShieldCheck, MessageSquare, CheckCircle2, XCircle, Ban, Flag } from "lucide-react";
import { cn } from "@/lib/utils";
import { statusClasses } from "@/lib/ui-theme";
import { useWeeklyHealth } from "@/hooks/use-weekly-health";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { isTerminalHealth } from "@/types/rocks";

interface WeeklyUpdatesTimelineProps {
  rockId: string;
  period?: string;
  currentWeek?: number;
  preloadedSnapshots?: import("@/types/rocks").WeeklySnapshot[];
  className?: string;
}

const HEALTH_CONFIG = {
  on_track: { label: "On Track", icon: TrendingUp, dotClass: statusClasses.good.bgSolid, textClass: statusClasses.good.text },
  off_track: { label: "Off Track", icon: TrendingDown, dotClass: statusClasses.bad.bgSolid, textClass: statusClasses.bad.text },
  committed: { label: "Committed", icon: ShieldCheck, dotClass: statusClasses.caution.bgSolid, textClass: statusClasses.caution.text },
  // Terminal disposition states — written at quarter close. These live in
  // rock_weekly_snapshots at the slot just past the weekly grid (total_weeks+1).
  complete: { label: "Complete", icon: CheckCircle2, dotClass: statusClasses.good.bgSolid, textClass: statusClasses.good.text },
  done: { label: "Done", icon: CheckCircle2, dotClass: statusClasses.good.bgSolid, textClass: statusClasses.good.text },
  not_done: { label: "Not Done", icon: XCircle, dotClass: statusClasses.bad.bgSolid, textClass: statusClasses.bad.text },
  dropped: { label: "Dropped", icon: Ban, dotClass: "bg-muted-foreground", textClass: "text-muted-foreground" },
} as const;

const ALLOWED_HEALTHS = new Set<string>([
  "on_track",
  "off_track",
  "committed",
  "complete",
  "done",
  "not_done",
  "dropped",
]);

export function WeeklyUpdatesTimeline({ rockId, period, currentWeek, preloadedSnapshots, className }: WeeklyUpdatesTimelineProps) {
  const { snapshots, isLoading } = useWeeklyHealth(rockId, period, currentWeek, preloadedSnapshots);
  const [isOpen, setIsOpen] = useState(false);

  // Keep weekly check-ins AND terminal-disposition snapshots. Disposition rows
  // live at the slot past the weekly grid (week_number = total_weeks + 1) and
  // carry the closing note set when the rock was marked complete/done/etc.
  const weekly = snapshots.filter((s) => ALLOWED_HEALTHS.has(s.health));

  // Sort descending — disposition entry naturally lands on top because its
  // week_number is the highest.
  const sorted = [...weekly].sort((a, b) => b.week_number - a.week_number);

  if (isLoading) return null;

  const updatesWithNotes = sorted.filter((s) => s.notes);
  const hasUpdates = sorted.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn("rounded-lg border", className)}>
      <CollapsibleTrigger className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold text-xs">WEEKLY UPDATES</span>
          {hasUpdates && (
            <span className="text-[10px] text-muted-foreground ml-1">
              {sorted.length} week{sorted.length !== 1 ? "s" : ""}
              {updatesWithNotes.length > 0 && ` · ${updatesWithNotes.length} with notes`}
            </span>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        {!hasUpdates && (
          <p className="text-xs text-muted-foreground italic mt-1">
            None yet.
          </p>
        )}
        <div className="ml-2 border-l border-muted-foreground/20 pl-3 mt-1 space-y-0">
          {sorted.map((snap) => {
            const health = snap.health as keyof typeof HEALTH_CONFIG;
            const config = HEALTH_CONFIG[health];
            const isTerminal = isTerminalHealth(snap.health);
            const isCurrent = !isTerminal && currentWeek !== undefined && snap.week_number === currentWeek;

            return (
              <div key={snap.week_number} className="relative py-1.5">
                {/* Timeline dot — flag icon for the terminal closing event so it stands out */}
                {isTerminal ? (
                  <Flag
                    className={cn(
                      "absolute -left-[22px] top-[7px] h-3.5 w-3.5",
                      config?.textClass ?? "text-muted-foreground",
                    )}
                    fill="currentColor"
                  />
                ) : (
                  <div
                    className={cn(
                      "absolute -left-[19px] top-[10px] h-2.5 w-2.5 rounded-full border-2 border-background",
                      config?.dotClass ?? "bg-gray-400",
                    )}
                  />
                )}

                <div className="flex items-start gap-2">
                  {/* Label + health */}
                  <div className="flex items-center gap-1.5 min-w-[120px]">
                    <span
                      className={cn(
                        "text-xs font-medium",
                        isCurrent && "text-amber-600 dark:text-amber-400",
                        isTerminal && "italic",
                      )}
                    >
                      {isTerminal ? "Closing note" : `Week ${snap.week_number}`}
                      {isCurrent && " (now)"}
                    </span>
                    {config && (
                      <span className={cn("text-[10px] font-medium", config.textClass)}>
                        {config.label}
                      </span>
                    )}
                  </div>

                  {/* Saved by */}
                  {snap.saved_by && (
                    <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                      {snap.saved_by}
                    </span>
                  )}
                </div>

                {/* Notes */}
                {snap.notes && (
                  <p className="text-xs text-muted-foreground mt-0.5 ml-0 leading-relaxed">
                    {snap.notes}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
